import {
  AggregationType,
  ColumnValueSegment,
  IDatasetLensColumn,
  IReportDatasetLens,
  IReportDatasetLensResponse,
  MultiHierarchyCoaFilter
} from '@opengov/reporting-types';
import { Dispatch, SetStateAction } from 'react';
import { LocaleText } from '../../../../locale';
import { IColumnNameFilters } from '../../../../types/MultiDatasetTypes';
import {
  DateFieldOption,
  IColumnItem,
  IFormDateRangeData,
  IInitialStateUseFilterForm
} from '../../filterForm/hooks/interfaces';

import { IColumnOptionSegment } from '../../../../types/ColumnOptionTypes';

export type GroupByConfig =
  | {
      variant: 'dimension';
      groupBy: string;
      groupByDimensionId?: string;
    }
  | {
      variant: 'multi-hierarchy';
      segmentId: string;
      groupId: string;
    }
  | {
      // "Organize columns by Account Number": scoped like a multi-hierarchy segment
      // column (segmentId/groupId) but enumerates leaf account_number_id values.
      variant: 'multi-hierarchy-account-number';
      segmentId: string;
      groupId: string;
    };

export type MultiHierarchyGroupByConfig = Extract<GroupByConfig, { variant: 'multi-hierarchy' }>;

export type MultiHierarchyAccountNumberGroupByConfig = Extract<
  GroupByConfig,
  { variant: 'multi-hierarchy-account-number' }
>;

export function isMultiHierarchyGroupByConfig(
  groupByConfig: GroupByConfig
): groupByConfig is MultiHierarchyGroupByConfig {
  return groupByConfig.variant === 'multi-hierarchy';
}

export function isMultiHierarchyAccountNumberGroupByConfig(
  groupByConfig: GroupByConfig
): groupByConfig is MultiHierarchyAccountNumberGroupByConfig {
  return groupByConfig.variant === 'multi-hierarchy-account-number';
}

export interface IColumnEditorSetupFormValues {
  datasourceId: string;
  amount: string;
  groupByConfig: GroupByConfig;
  name: string;
  aggregation?: AggregationType;
}

export interface IColumnHeaderNameValidation {
  isValid: boolean;
  invalidString: string;
}

export interface IColumnEditorSetupProps extends IColumnEditorSetupFormValues {
  /**
   * Every organize-by level of the loaded column group, outermost first. `groupByConfig` remains the
   * first level, for the consumers that only ever deal with one. Optional: callers that only know
   * one level (override row editor, single-datasource column editor) omit it.
   */
  groupByConfigs?: GroupByConfig[];
  amountOptions: IDatasetLensColumn[];
  columnEditorFunctions?: Record<string, Dispatch<SetStateAction<string>>>;
  columnHeaderUnique?: boolean;
  columnHeaderValid?: IColumnHeaderNameValidation;
  columnsLoading: boolean;
  datasetLenses: IReportDatasetLensResponse[];
  /** Ids of every entity lens, used to tell a deleted data source from a merely incompatible one. */
  entitySourceIds?: string[];
  defaultValuesForDatasource(dataSourceId: string): Promise<Partial<IColumnEditorSetupFormValues>>;
  existingColumnHeaders: string[];
  groupByOptions: IColumnOptionSegment[];
  hideName?: boolean;
  hideColumnGroupHeaders?: boolean;
  lensesLoading: boolean;
  lenses: IReportDatasetLens[];
  onChange?(values: Partial<IColumnEditorSetupProps>): void;
  preparePayload(setupPayload: IColumnEditorSetupFormValues): {
    datasourceId: string;
    amount: string;
    name: string;
    aggregation?: AggregationType;
    groupByConfig: GroupByConfig;
  };
  localeText: LocaleText['multidataset']['columnEditorSection'];
  aggregation?: AggregationType;
  columnValueName?: string;
  selectedSegment?: ColumnValueSegment[];
  isDuplicateColumn?: boolean;
}

export interface IColumnGroupValue extends IColumnEditorSetupFormValues {
  initialFilters: IInitialStateUseFilterForm;
  filterConditions: IColumnNameFilters;
  dateRangeData: IFormDateRangeData;
}

export interface IOverrideRowEditorProps {
  initialColumnsFormValues: Record<string, IColumnGroupValue>;
  columnsProps: IOverrideRowEditorColumnsProps;
  getColumnEditorFormOptions(lensId: string): { amountOptions: any[]; groupByOptions: any[] };
}

/**
 * ERP (multi-hierarchy) counterpart of `IColumnGroupValue`. Carries multi-hierarchy CoA filters
 * directly instead of the dimension-based filter form state used by non-ERP reports.
 */
export interface IMultiHierarchyColumnGroupValue extends IColumnEditorSetupFormValues {
  filters: MultiHierarchyCoaFilter[];
  dateRangeData: IFormDateRangeData;
}

export type IOverrideRowEditorColumnsProps = {
  amountColumns: IReportDatasetLens[];
  existingColumnHeaders: string[];
  datasetLenses: IReportDatasetLensResponse[];
  lensesLoading: boolean;
  lenses: IReportDatasetLens[];
  loadingLensesWithColumnIds: string[];
  defaultValuesForDatasource(dataSourceId: string): Promise<Partial<IColumnEditorSetupFormValues>>;
  preparePayload(setupPayload: IColumnEditorSetupFormValues): IColumnEditorSetupFormValues;
};

export interface IOverrideRowColumnEditorFilter {
  getAvailableColumnsForLens(lensId: string): IColumnItem[];
  getFilterDateRangeForLens(lensId: string): DateFieldOption[];
  preparePayload: IPreparePayload;
  currentReportId?: string;
  fiscalYearStartMonth?: number;
}

export interface IPreparePayload {
  (filters: IColumnNameFilters): Record<string, any>;
}
